import type { BlockField, SignatureContent } from '@/blocks/domain/types';
import type { RendererProps } from '../../domain/RendererContract';
import { FieldsBlockRender } from './FieldsBlockRender';
import { useQuoteCanvasEditing } from '@/document-render/presentation/editing/quote/QuoteCanvasEditingContext';
import { useDocumentAudience } from '@/document-render/presentation/primitives/useDocumentAudience';
import { SignatureQuoteLine } from './signature/SignatureQuoteLine';

type Props = RendererProps<SignatureContent>;

/**
 * The seller's per-line eye, laid OVER its signature field (absolutely positioned in the field's
 * own relative box) rather than appended under the printed fields — so toggling the canvas chrome
 * never changes the block's height or the page break it sits on.
 */
function SignatureRenderImpl({ content, style, context, accentColor, knownPaths }: Props) {
  const quote = useQuoteCanvasEditing();
  const audience = useDocumentAudience();
  const onToggle = quote?.kind === 'signature' && audience !== 'customer' ? quote.editing.onToggleSignatureFieldHidden : undefined;
  const stored = content.fields ?? [];
  // The seller keeps seeing a line they hid (dimmed, with its eye) so it can be shown again; the
  // customer view and the PDF drop it through the ordinary `visible` filter.
  const fields = onToggle ? stored.map((field) => (field.style === 'signature_line' ? { ...field, visible: true } : field)) : stored;
  const hiddenIds = new Set(stored.filter((field) => field.visible === false).map((field) => field.id));
  const renderFieldOverlay = onToggle
    ? (field: BlockField) =>
        field.style === 'signature_line' ? (
          <div style={{ position: 'absolute', top: 0, right: 0 }} data-signature-eye-overlay={field.id}>
            <SignatureQuoteLine
              fieldId={field.id}
              label={field.label || 'signature'}
              visible={!hiddenIds.has(field.id)}
              onToggle={onToggle}
            />
          </div>
        ) : null
    : undefined;

  return (
    <FieldsBlockRender
      fields={fields}
      style={style}
      context={context}
      accentColor={accentColor}
      knownPaths={knownPaths}
      ariaLabel="block-signature"
      sections={content.sections}
      renderFieldOverlay={renderFieldOverlay}
      isFieldDimmed={onToggle ? (field) => hiddenIds.has(field.id) : undefined}
    />
  );
}

export const renderer = {
  Component: SignatureRenderImpl,
  flowBehavior: 'atomic' as const
};

export type { SignatureContent };
